/**
 * AsOfPicker -- time-travel control (P2.8.c).
 *
 * Datetime-local input + Apply + (when active) Return-to-live.
 * Reads + writes URL search param `as_of` via the useAsOf hooks.
 *
 * Visual states:
 *   - Inactive (no ?as_of=): slate outline, "Viewing live state" copy.
 *   - Active (?as_of= set): amber outline, "Viewing as of <when>"
 *     copy + Return-to-live button.
 *
 * Draft + Apply: the input commits to URL state only when Apply is
 * pressed. Avoids re-fetches on every keystroke. v1 convention --
 * the amber colour code matches v1 so a customer cross-referencing
 * v1 demos sees the same signal.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAsOf, useAsOfSetter } from "shared/hooks/useAsOf";
import { formatIsoForDisplay, naiveLocalToIsoOffset } from "shared/time/timeInputs";

export function AsOfPicker() {
  const { t, i18n } = useTranslation();
  const asOf = useAsOf();
  const setAsOf = useAsOfSetter();
  const [draft, setDraft] = useState<string>("");

  // Clear the draft when the active asOf is cleared externally
  // (e.g. user navigates away via "Return to live" or a future
  // back-button + restored URL state).
  useEffect(() => {
    if (asOf === null) {
      setDraft("");
    }
  }, [asOf]);

  const active = asOf !== null;
  const onApply = () => {
    const iso = naiveLocalToIsoOffset(draft.trim().replace(" ", "T"));
    if (iso !== null) {
      setAsOf(iso);
    }
  };
  // What the reader typed, judged before they press anything: a field
  // that accepts anything and then silently does nothing on Apply is
  // the same dead end in a new shape.
  const parseable = naiveLocalToIsoOffset(draft.trim().replace(" ", "T")) !== null;
  const onClear = () => setAsOf(null);

  return (
    <section
      data-testid="as-of-picker"
      data-active={active ? "true" : "false"}
      className={[
        "rounded-md border px-3 py-2 text-xs mb-4",
        active
          ? "border-amber-400 bg-amber-50 text-amber-900"
          : "border-slate-200 bg-white text-slate-700",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="as-of-picker-status"
          className="font-medium uppercase tracking-wide text-[10px] opacity-80"
        >
          {active
            ? t("modules.twin.asOfPicker.activeLabel", {
                when: formatIsoForDisplay(asOf, i18n.language),
              })
            : t("modules.twin.asOfPicker.liveLabel")}
        </span>
        <label className="ms-auto flex items-center gap-2">
          <span className="sr-only">{t("modules.twin.asOfPicker.inputLabel")}</span>
          {/*
           * Round 10 D3, eighth round of this finding -- and the fix
           * as shipped could not work.
           *
           * A native date control takes its segment order from the
           * BROWSER's locale and ignores the element's `lang`.
           * Measured rather than argued: four `type="date"` inputs --
           * bare, lang="en-GB", lang="ar-SA", and one inheriting
           * lang from a wrapper -- render IDENTICALLY in one browser.
           * So `useDateInputLang`, which round 9 put on seventeen
           * controls, cannot change what any of them shows, and the
           * reader on a US-configured browser keeps getting mm/dd/yyyy
           * under a page whose every other date reads "30 Aug 2026".
           *
           * A text field in ISO order is the honest replacement: it is
           * the format the API takes, the format the chain rows print,
           * and the one order no locale reads differently. The format
           * is STATED beside the field rather than implied by a
           * browser setting nobody can see.
           */}
          <input
            dir="ltr"
            data-testid="as-of-picker-input"
            type="text"
            inputMode="numeric"
            placeholder={t("modules.twin.asOfPicker.format")}
            aria-describedby="as-of-picker-format"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-[11.5rem] rounded border border-slate-300 bg-white px-2 py-1 font-mono text-xs"
          />
        </label>
        <span
          id="as-of-picker-format"
          data-testid="as-of-picker-format"
          className="font-mono text-[10px] opacity-70"
        >
          {t("modules.twin.asOfPicker.format")}
        </span>
        <button
          type="button"
          data-testid="as-of-picker-apply"
          onClick={onApply}
          disabled={!parseable}
          title={
            draft && !parseable ? t("modules.twin.asOfPicker.formatHint") : undefined
          }
          className={[
            "rounded px-2 py-1 text-xs font-medium",
            parseable
              ? "bg-brand-brass text-brand-navy hover:bg-brand-brassDark"
              : "bg-slate-200 text-slate-500 cursor-not-allowed",
          ].join(" ")}
        >
          {t("modules.twin.asOfPicker.applyButton")}
        </button>
        {active && (
          <button
            type="button"
            data-testid="as-of-picker-clear"
            onClick={onClear}
            className="rounded px-2 py-1 text-xs font-medium bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300"
          >
            {t("modules.twin.asOfPicker.returnToLiveButton")}
          </button>
        )}
      </div>
    </section>
  );
}
